import { PortalStage } from '../types';

export interface JourneyQuestion {
  id: string;
  question: string;
  answer: string;
}

export interface JourneyTestimonial {
  name: string;
  location: string;
  year: number;
  quote: string;
  projectType: string;
  photoUrl?: string;
  videoUrl?: string;
}

export interface JourneyDeliverable {
  label: string;
  description: string;
}

export interface JourneyStage {
  id: PortalStage;
  number: number;
  title: string;
  subtitle: string;
  duration: string;
  leadQuestion: string;
  leadAnswer: string;
  questions: JourneyQuestion[];
  testimonial: JourneyTestimonial;
  deliverables: JourneyDeliverable[];
  nextStageTeaser?: string;
}

export const JOURNEY_STAGES: JourneyStage[] = [
  {
    id: 'design-fee',
    number: 1,
    title: 'Concept Design',
    subtitle: 'Where your timber frame begins to take shape',
    duration: '1–2 weeks',
    leadQuestion: 'What am I actually paying for at this stage?',
    leadAnswer: 'The concept design fee covers your dedicated design time with our team. We take your floor plans, inspirations, and wish list and translate them into an initial timber frame layout that fits your home. This isn\'t a generic template — it\'s custom work from day one.',
    questions: [
      {
        id: 'df-1',
        question: 'How much is the concept design fee?',
        answer: 'The fee varies based on the complexity and size of your project. We\'ll discuss this during your initial consultation so there are no surprises. It\'s a fraction of the overall project cost and is applied toward your final quote.',
      },
      {
        id: 'df-2',
        question: 'What if I change my mind after paying?',
        answer: 'The design fee covers the work our team puts into your concept. If you decide not to proceed, you keep the concept designs we\'ve created. Many families use them as a starting point even if their timeline shifts.',
      },
      {
        id: 'df-3',
        question: 'What do you need from me to get started?',
        answer: 'Your floor plans (even rough sketches work), photos of timber frames you love, and a conversation about how you want the frame to feel in your home. We handle the rest.',
      },
      {
        id: 'df-4',
        question: 'Can my architect or builder be involved?',
        answer: 'Absolutely — and we encourage it. The earlier your team is involved, the smoother the entire process. We collaborate directly with architects and builders on every project.',
      },
    ],
    testimonial: {
      name: 'The Harrington Family',
      location: 'Stowe, Vermont',
      year: 2024,
      quote: 'We were nervous about the initial investment, but seeing our concept come to life in the first week made it real. That\'s when we knew we\'d made the right call.',
      projectType: 'Mountain Retreat',
    },
    deliverables: [
      { label: 'Concept Layout', description: 'Initial timber frame design tailored to your floor plan' },
      { label: 'Design Consultation', description: 'One-on-one session reviewing your vision and preferences' },
      { label: 'Preliminary Scope', description: 'Overview of frame size, species, and joinery approach' },
    ],
    nextStageTeaser: 'Next, your design becomes something you can explore in three dimensions.',
  },
  {
    id: '3d-model',
    number: 2,
    title: '3D Model',
    subtitle: 'See your frame before a single timber is cut',
    duration: '2–3 weeks',
    leadQuestion: 'Will I actually be able to see what my frame looks like?',
    leadAnswer: 'Yes — in full 3D. We build a detailed digital model of your timber frame that you can rotate, zoom into, and explore from every angle. You\'ll see the joinery, the timber sizes, and exactly how the frame sits within your home. Most families say this is the moment it stops being an idea and starts feeling like their home.',
    questions: [
      {
        id: '3d-1',
        question: 'Can I request changes to the 3D model?',
        answer: 'That\'s exactly what this stage is for. We refine the model with your feedback until it\'s right. Want to raise the ridge? Widen a bent? Add a king post? This is where we dial it in.',
      },
      {
        id: '3d-2',
        question: 'Can I share the model with my builder and architect?',
        answer: 'Yes. We provide shareable links and can export files in formats your builder and architect can work with. Everyone stays on the same page.',
      },
      {
        id: '3d-3',
        question: 'How realistic is the visualization?',
        answer: 'Very. You\'ll see actual timber dimensions, joinery details, and how the frame integrates with your roof and floor plans. It\'s an engineering model, not a rough sketch.',
      },
      {
        id: '3d-4',
        question: 'Do I need any special software to view it?',
        answer: 'No. The model is viewable in any web browser on your phone, tablet, or computer. Just click the link we send you.',
      },
    ],
    testimonial: {
      name: 'David & Sarah Chen',
      location: 'Lake Sunapee, NH',
      year: 2023,
      quote: 'Spinning that 3D model around on my phone while sitting on our building lot — that was the moment my wife looked at me and said, "We\'re really doing this." It made everything tangible.',
      projectType: 'Lakefront Home',
    },
    deliverables: [
      { label: 'Interactive 3D Model', description: 'Full digital model viewable on any device' },
      { label: 'Design Revisions', description: 'Iterative refinement based on your feedback' },
      { label: 'Shareable Links', description: 'Send to your builder, architect, and family' },
    ],
    nextStageTeaser: 'With the design locked in, we put together your complete quote.',
  },
  {
    id: 'quote',
    number: 3,
    title: 'Your Quote',
    subtitle: 'Complete pricing with no surprises',
    duration: '1–2 weeks',
    leadQuestion: 'How is the pricing actually structured?',
    leadAnswer: 'Your quote is a detailed, line-by-line breakdown. You\'ll see the cost of every timber, every connection, and every hour of labor. We don\'t bundle things into vague line items. If a timber is 8×10 Douglas Fir, 16 feet long — that\'s exactly what you\'ll see. Transparency builds trust, and that\'s how we operate.',
    questions: [
      {
        id: 'q-1',
        question: 'What\'s included in the quote?',
        answer: 'Materials (timbers, fasteners, connectors), CNC cutting and fabrication, joinery, shop assembly and test-fit, delivery to your site, and our crew for the raising. Everything needed to go from raw timber to a standing frame.',
      },
      {
        id: 'q-2',
        question: 'Are there ever costs beyond the quote?',
        answer: 'Only if you request changes after signing. The quote covers everything we\'ve agreed to. If your site requires special logistics (like a mountain road or limited crane access), we\'ll flag that upfront.',
      },
      {
        id: 'q-3',
        question: 'How long is the quote valid?',
        answer: 'Typically 30 days. Timber prices fluctuate with the market, so we lock in pricing for that window. If your timeline is longer, we can discuss options.',
      },
      {
        id: 'q-4',
        question: 'Can I adjust the scope to fit my budget?',
        answer: 'Absolutely. We can explore different timber species, simplify joinery in less visible areas, or adjust the frame\'s footprint. Our goal is to find the right frame for your budget, not upsell you.',
      },
    ],
    testimonial: {
      name: 'Mark & Lisa Andersen',
      location: 'Killington, Vermont',
      year: 2024,
      quote: 'The quote was the most transparent thing I\'ve seen in 18 months of building. Every other contractor gave us a lump sum. LC gave us a spreadsheet we could actually understand.',
      projectType: 'Ski Chalet',
    },
    deliverables: [
      { label: 'Detailed Quote', description: 'Line-by-line pricing for your complete timber frame' },
      { label: 'Scope Document', description: 'Exact specifications of materials, joinery, and services' },
      { label: 'Options Summary', description: 'Alternatives and adjustments if you want to explore them' },
    ],
    nextStageTeaser: 'Ready to commit? Here\'s what signing looks like.',
  },
  {
    id: 'quote-signed',
    number: 4,
    title: 'Agreement Signed',
    subtitle: 'Your project is officially in motion',
    duration: 'Same day',
    leadQuestion: 'What happens the moment I sign?',
    leadAnswer: 'Your project gets a production slot. We order your timbers, assign your project manager, and begin detailed engineering. This is the point where we go from "planning your frame" to "building your frame." You\'ll have a direct line to your project manager from here forward.',
    questions: [
      {
        id: 'qs-1',
        question: 'What\'s the deposit structure?',
        answer: 'A deposit is due at signing to secure your production slot and begin material procurement. The remaining balance is structured around production milestones. We\'ll walk through the exact schedule before you sign.',
      },
      {
        id: 'qs-2',
        question: 'Can I make design changes after signing?',
        answer: 'Minor adjustments are usually possible early on. Larger changes may affect pricing and timeline. Your project manager will be upfront about what\'s flexible and what\'s locked.',
      },
      {
        id: 'qs-3',
        question: 'When does production actually start?',
        answer: 'Engineering begins immediately. Production timing depends on your delivery date and our current schedule. We\'ll give you a clear timeline at signing so you can coordinate with your builder.',
      },
      {
        id: 'qs-4',
        question: 'Who is my main point of contact going forward?',
        answer: 'Your dedicated project manager. They\'ll be with you through engineering, production, and delivery. One person who knows your project inside and out.',
      },
    ],
    testimonial: {
      name: 'The Patel Family',
      location: 'Woodstock, Vermont',
      year: 2023,
      quote: 'Signing felt like the easiest decision of the whole build. By that point, we\'d seen the 3D model, understood every line of the quote, and trusted the team completely.',
      projectType: 'Family Homestead',
    },
    deliverables: [
      { label: 'Signed Agreement', description: 'Your confirmed project scope and pricing' },
      { label: 'Production Slot', description: 'Reserved place in our fabrication schedule' },
      { label: 'Project Manager', description: 'Your dedicated point of contact assigned' },
    ],
    nextStageTeaser: 'Now our engineers get to work on the precision details.',
  },
  {
    id: '2d-plans',
    number: 5,
    title: 'Engineered Drawings',
    subtitle: 'Every joint, every connection, engineered to last',
    duration: '3–5 weeks',
    leadQuestion: 'How detailed are the construction plans?',
    leadAnswer: 'Our engineered drawings are the blueprint your builder will work from. Every timber is dimensioned, every joint is detailed, every connection is specified. They include structural engineering stamps, connection details, hardware schedules, and assembly sequences. Your builder won\'t have to guess about anything.',
    questions: [
      {
        id: 'ed-1',
        question: 'Who does the structural engineering?',
        answer: 'We work with licensed structural engineers who specialize in timber frame construction. The drawings are stamped and approved for your specific building code jurisdiction.',
      },
      {
        id: 'ed-2',
        question: 'Will my builder be able to work from these plans?',
        answer: 'Yes. Our drawings are designed for builders, not just engineers. Clear dimensions, labeled connections, and a logical assembly sequence. We also do a pre-construction call with your builder to walk through everything.',
      },
      {
        id: 'ed-3',
        question: 'Do the plans cover the SIP or enclosure system?',
        answer: 'If we\'re providing the enclosure (SIPs, purlins, etc.), those details are included. If your builder is handling the enclosure, we coordinate to make sure everything aligns.',
      },
      {
        id: 'ed-4',
        question: 'How long does the engineering phase take?',
        answer: 'Typically 3–5 weeks, depending on complexity. We\'ll share progress and flag any questions that come up during engineering so there are no delays.',
      },
    ],
    testimonial: {
      name: 'James & Emily Richardson',
      location: 'Freeport, Maine',
      year: 2024,
      quote: 'Our builder said the engineered drawings were the best he\'d ever worked with. Every connection was spelled out. He didn\'t have to call once during the raising.',
      projectType: 'Coastal Cape',
    },
    deliverables: [
      { label: 'Stamped Engineering', description: 'PE-stamped structural drawings for your jurisdiction' },
      { label: 'Connection Details', description: 'Every joint specified with hardware and fasteners' },
      { label: 'Assembly Sequence', description: 'Step-by-step guide for your raising crew' },
    ],
    nextStageTeaser: 'With plans finalized, your timbers hit the CNC.',
  },
  {
    id: 'in-production',
    number: 6,
    title: 'In Production',
    subtitle: 'Your frame takes physical form',
    duration: '4–8 weeks',
    leadQuestion: 'Can I see my frame being built?',
    leadAnswer: 'Yes. We send photo and video updates as your frame moves through our shop. You\'ll see your timbers selected, cut on our CNC, joinery test-fit, and the frame pre-assembled in our facility before it ever leaves for your site. Some families visit the shop in person — you\'re always welcome.',
    questions: [
      {
        id: 'ip-1',
        question: 'How long does production take?',
        answer: 'Typically 4–8 weeks depending on the size and complexity of your frame. Your project manager will give you a specific timeline when production begins.',
      },
      {
        id: 'ip-2',
        question: 'What wood species do you use?',
        answer: 'Douglas Fir, Eastern White Pine, White Oak, and Red Oak are the most common. The right species depends on your design, structural needs, and aesthetic preference. We\'ll recommend what makes sense for your project.',
      },
      {
        id: 'ip-3',
        question: 'How do you ensure quality?',
        answer: 'Every timber is graded, every joint is test-fit in our shop before disassembly and shipping. We pre-assemble the critical sections to verify fit. If it doesn\'t come together perfectly in the shop, it doesn\'t ship.',
      },
      {
        id: 'ip-4',
        question: 'Can I visit the shop?',
        answer: 'You\'re welcome any time. Seeing your frame come together in person is something most families never forget. Just coordinate with your project manager so we can plan the best time to visit.',
      },
    ],
    testimonial: {
      name: 'Robert & Anne Whitfield',
      location: 'Stowe, Vermont',
      year: 2023,
      quote: 'We drove up to the shop when our frame was assembled on the floor. Watching our kids run between the timbers, touching the wood that would become their home — that was everything.',
      projectType: 'Year-Round Retreat',
    },
    deliverables: [
      { label: 'Progress Updates', description: 'Regular photo and video updates from the shop' },
      { label: 'Pre-Assembly', description: 'Your frame test-fit in our facility before shipping' },
      { label: 'Quality Report', description: 'Inspection documentation for every timber' },
    ],
    nextStageTeaser: 'The moment you\'ve been waiting for — raising day.',
  },
  {
    id: 'delivery',
    number: 7,
    title: 'Delivery & Raising',
    subtitle: 'The day your frame stands',
    duration: '1–3 days on site',
    leadQuestion: 'How does raising day actually work?',
    leadAnswer: 'A flatbed arrives with your labeled, pre-cut, pre-fit timbers. Our crew and a crane methodically assemble your frame — typically in 1–3 days depending on size. It\'s the most dramatic day of any build. Families gather, cameras come out, and in a matter of hours, the skeleton of your home is standing against the sky.',
    questions: [
      {
        id: 'd-1',
        question: 'How is the frame transported?',
        answer: 'By flatbed truck, with every timber labeled and packed in assembly order. We coordinate delivery timing with your builder so the site is prepped and the crane is scheduled.',
      },
      {
        id: 'd-2',
        question: 'Do you handle the crane?',
        answer: 'We coordinate the crane as part of the project. Our crew has raised hundreds of frames and knows exactly what equipment is needed for your specific project and site conditions.',
      },
      {
        id: 'd-3',
        question: 'How long does the raising take?',
        answer: 'Most residential frames go up in 1–3 days. Larger or more complex frames may take a bit longer. Your project manager will give you a specific schedule.',
      },
      {
        id: 'd-4',
        question: 'Should I be there for the raising?',
        answer: 'We strongly encourage it. Raising day is one of the most memorable experiences of building a home. Bring your family, your friends, your camera. This is the day it all comes together.',
      },
    ],
    testimonial: {
      name: 'Tom & Michelle Bergström',
      location: 'Waitsfield, Vermont',
      year: 2024,
      quote: 'We stood in our field with coffee at 7am and by sunset, our frame was standing. Our daughter asked if she could sleep in it that night. That\'s when it hit us — we built a home.',
      projectType: 'Timber Frame Barn Home',
    },
    deliverables: [
      { label: 'Site Delivery', description: 'Complete timber package delivered to your building site' },
      { label: 'Raising Crew', description: 'Our experienced team on-site for assembly' },
      { label: 'Standing Frame', description: 'Your timber frame, raised and secured' },
    ],
    nextStageTeaser: 'Ready to take the next step? See how to get started.',
  },
];

export interface JourneyTrustStats {
  familiesServed: number;
  yearsInBusiness: number;
  averageRating: number;
  reviewCount: number;
  statesServed: number;
}

export const TRUST_STATS: JourneyTrustStats = {
  familiesServed: 52,
  yearsInBusiness: 11,
  averageRating: 4.9,
  reviewCount: 38,
  statesServed: 8,
};
